import React, { useEffect, useMemo, useState } from "react";
import api from "pages/api/api";
import { useAuth } from "pages/context/AuthContext";
import { useTable, useFilters } from "react-table";
import Draggable from "react-draggable";

import {
  Card,
  TextField,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

function DefaultColumnFilter({ column }) {
  const { filterValue, setFilter } = column;
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        padding: "2px 4px",
        fontSize: 11,
        border: "none",
        borderBottom: "1px solid #e0e0e0",
        outline: "none",
        backgroundColor: "transparent",
      }}
      placeholder="Filtrar."
    />
  );
}

function pickMetricKey(obj) {
  if (!obj) return "qtd";

  if (Number(obj.qtd || 0) > 0) return "qtd";
  if (Number(obj.euros || 0) > 0) return "euros";
  if (Number(obj.litros || 0) > 0) return "litros";
  if (Number(obj.clientes || 0) > 0) return "clientes";

  const nome = String(obj.objetivo_nome || obj.nome || "").toLowerCase();

  if (nome.includes("litro")) return "litros";
  if (nome.includes("dns")) return "clientes";
  if (nome.includes("cliente")) return "clientes";
  if (nome.includes("euro")) return "euros";

  return "qtd";
}

function metricLabel(k) {
  if (k === "qtd") return "qtd";
  if (k === "euros") return "euros";
  if (k === "litros") return "litros";
  if (k === "clientes") return "clientes";
  return k;
}

function toISO(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toISOString().slice(0, 10);
}

function isActiveToday(obj) {
  const today = new Date();
  const di = new Date(obj.dataini);
  const df = new Date(obj.datafim);
  if (Number.isNaN(di.getTime()) || Number.isNaN(df.getTime())) return false;
  // hoje entre [dataini, datafim]
  return di <= today && today <= df;
}

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

function temObjetivoDefinido(value) {
  const v = safeNum(value);
  return v > 0;
}

function pickMetricKeyFromDetalhes(det, fallback = "qtd") {
  const keys = ["qtd", "euros", "litros", "clientes"];

  // 1) tenta encontrar a métrica que tem OBJETIVO > 0 em alguma secção
  for (const k of keys) {
    const objFab = safeNum(det?.fabricante?.[k]?.objetivo);
    const objEq = safeNum(det?.equipa?.[k]?.objetivo);
    const objInd = safeNum(det?.individuais?.[k]?.objetivo);

    if (objFab > 0 || objEq > 0 || objInd > 0) return k;
  }

  // 2) se objetivos forem 0 (casos especiais), usa a primeira com REAL > 0
  for (const k of keys) {
    const venFab = safeNum(det?.fabricante?.[k]?.vendas);
    const venEq = safeNum(det?.equipa?.[k]?.vendas);
    const venInd = safeNum(det?.individuais?.[k]?.vendas);

    if (venFab > 0 || venEq > 0 || venInd > 0) return k;
  }

  return fallback;
}

function VendedoresMiniTable({ vendedores = [], tipoKey = "qtd" }) {
  const rows = Array.isArray(vendedores) ? vendedores : [];

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const fmt = (n) => {
    const v = safeNum(n);
    // mostra 0 sem casas; se quiseres sempre 2 casas, muda para toFixed(2)
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  };

  const calc = rows
    .map((v) => {
      const met = v?.[tipoKey] || {};
      const objetivo = safeNum(met.objetivo);
      const real = safeNum(met.vendas);
      if (objetivo <= 0) return null; // só mostra vendedores com objetivo definido
      const dif = real - objetivo;
      const perc = objetivo > 0 ? (real / objetivo) * 100 : null;

      return {
        key: v?.refvendedor || v?.vendedor || Math.random(),
        vendedor: v?.vendedor || v?.refvendedor || "",
        objetivo,
        real,
        dif,
        perc,
      };
    })
    .filter(Boolean);

  const totalObj = calc.reduce((a, r) => a + safeNum(r.objetivo), 0);
  const totalReal = calc.reduce((a, r) => a + safeNum(r.real), 0);
  const totalDif = totalReal - totalObj;
  const totalPerc = totalObj > 0 ? (totalReal / totalObj) * 100 : null;

  return (
    <div>
      <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 8 }}>
        Individuais — detalhe por vendedor
      </div>

      <div style={{ maxHeight: 260, overflow: "auto", borderRadius: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={thMini}>Vendedor</th>
              <th style={thMini}>Objetivo</th>
              <th style={thMini}>Real</th>
              <th style={thMini}>Dif.</th>
              <th style={thMini}>%</th>
            </tr>
          </thead>

          <tbody>
            {calc.map((r) => (
              <tr key={r.key} style={{ borderBottom: "1px solid #f0f0f0" }}>
                <td style={tdMini}>{r.vendedor}</td>
                <td style={tdMini}>{fmt(r.objetivo)}</td>
                <td style={{ ...tdMini, fontWeight: 700 }}>{fmt(r.real)}</td>
                <td style={{ ...tdMini, color: r.dif >= 0 ? "#4caf50" : "#d32f2f" }}>
                  {r.dif.toFixed(2)}
                </td>
                <td style={tdMini}>{r.perc != null ? `${r.perc.toFixed(1)}%` : ""}</td>
              </tr>
            ))}

            {calc.length === 0 && (
              <tr>
                <td colSpan={5} style={{ ...tdMini, textAlign: "center", color: "#9e9e9e" }}>
                  Sem dados (nenhum vendedor com objetivo definido).
                </td>
              </tr>
            )}
          </tbody>

          {calc.length > 0 && (
            <tfoot>
              <tr>
                <td style={{ ...tdMini, fontWeight: 700 }}>Total</td>
                <td style={{ ...tdMini, fontWeight: 700 }}>{fmt(totalObj)}</td>
                <td style={{ ...tdMini, fontWeight: 700 }}>{fmt(totalReal)}</td>
                <td
                  style={{
                    ...tdMini,
                    fontWeight: 700,
                    color: totalDif >= 0 ? "#4caf50" : "#d32f2f",
                  }}
                >
                  {totalDif.toFixed(2)}
                </td>
                <td style={{ ...tdMini, fontWeight: 700 }}>
                  {totalPerc != null ? `${totalPerc.toFixed(1)}%` : ""}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function numFmt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function difCellMini(objetivo, real) {
  const o = safeNum(objetivo);
  const r = safeNum(real);
  if (!o) return <span>-</span>;

  const dif = r - o;
  const color = dif > 0 ? "#4caf50" : dif < 0 ? "#d32f2f" : undefined;

  return <span style={{ color, fontWeight: 500 }}>{numFmt(dif)}</span>;
}

function pctFmtMini(objetivo, real) {
  const o = safeNum(objetivo);
  const r = safeNum(real);
  if (!o) return "-";

  const pct = (r / o) * 100;
  return `${pct.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;
}

function EquipaResumoMiniTable({ secData }) {
  const rows = useMemo(() => {
    const tipos = [
      { tipo: "Qtd", key: "qtd" },
      { tipo: "Euros", key: "euros" },
      { tipo: "Litros", key: "litros" },
      { tipo: "Clientes", key: "clientes" },
    ];

    return tipos.map((t) => {
      const objetivo = safeNum(secData?.[t.key]?.objetivo);
      const vendas = safeNum(secData?.[t.key]?.vendas);
      return {
        key: t.key,
        tipo: t.tipo,
        objetivo,
        vendas,
      };
    });
  }, [secData]);

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 8 }}>Objetivos da Equipa</div>

      <div style={{ overflow: "auto", borderRadius: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={thMini}>Tipo</th>
              <th style={thMini}>Objetivo</th>
              <th style={thMini}>Real</th>
              <th style={thMini}>Dif.</th>
              <th style={thMini}>%</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => (
              <tr key={r.key} style={{ borderBottom: "1px solid #f0f0f0" }}>
                <td style={tdMini}>{r.tipo}</td>

                {/* igual ao Objetivos.js: mostra "X (qtd)" */}
                <td style={tdMini}>{`${numFmt(r.objetivo)} (qtd)`}</td>

                <td style={{ ...tdMini, fontWeight: 700 }}>{numFmt(r.vendas)}</td>

                <td style={tdMini}>{difCellMini(r.objetivo, r.vendas)}</td>

                <td style={tdMini}>{pctFmtMini(r.objetivo, r.vendas)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const thMini = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "#fff",
  textAlign: "left",
  padding: "6px 8px",
  fontSize: 12,
  color: "#7b809a",
  verticalAlign: "bottom",
  boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
};

const tdMini = {
  padding: "6px 8px",
  whiteSpace: "nowrap",
};

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

export default function ObjetivosEstadoDashboard() {
  const { userData, temFuncao } = useAuth();

  // ⚠️ Ajusta aqui para bater com o teu "isAdminLike" do Objetivos.js
  const isAdminLike =
    temFuncao("Administração") ||
    temFuncao("Informática") ||
    temFuncao("Tesouraria") ||
    temFuncao("Gestão Objetivos");

  const refUser = userData?.refvendedor ? String(userData.refvendedor) : "";
  const isVendedor = !!refUser;

  // isAdminLike aqui é só gate (poder ver o componente)
  const canSee = isAdminLike || isVendedor;

  const isComercialRestrito = !isAdminLike && isVendedor;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [carregado, setCarregado] = useState(false);
  const [erro, setErro] = useState("");

  const [openInfo, setOpenInfo] = useState(false);
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoRow, setInfoRow] = useState(null);
  const [infoDetalhes, setInfoDetalhes] = useState(null);
  const [infoEqRows, setInfoEqRows] = useState([]); // opcional: só usado para Equipa

  const abrirInfo = async (row) => {
    if (!row?.idobjetivo) return;

    setInfoRow(row);
    setInfoDetalhes(null);
    setInfoEqRows([]);
    setOpenInfo(true);

    setInfoLoading(true);
    try {
      const detRes = await api.post("Comercial/SelObjetivoDetalhesCache", { id: row.idobjetivo });
      const det = detRes.data?.data ?? detRes.data;

      setInfoDetalhes(det);
    } catch {
      setInfoDetalhes(null);
    } finally {
      setInfoLoading(false);
    }
  };

  async function loadFromCache() {
    if (!canSee) return;

    setLoading(true);
    setErro("");

    try {
      // 1) lista objetivos a partir da cache
      const bodyObj = isComercialRestrito
        ? { refvendedor: refUser, apenasVigentes: true }
        : { apenasVigentes: true };

      const resObj = await api.post("Comercial/SelObjetivosCache", bodyObj);
      const ativos = Array.isArray(resObj.data) ? resObj.data : resObj.data?.data || [];

      // console.log("SelObjetivosCache ativos", ativos);
      // console.log("SelObjetivosCache total", ativos.length);

      const detalhesAll = await Promise.all(
        ativos.map(async (o) => {
          try {
            const detRes = await api.post("Comercial/SelObjetivoDetalhesCache", { id: o.id });
            const det = detRes.data?.data ?? detRes.data;
            return { obj: o, det };
          } catch (err) {
            console.log("Falha SelObjetivoDetalhesCache", o?.id, err);
            return { obj: o, det: null };
          }
        })
      );

      // console.log("detalhesAll", detalhesAll);

      const out = [];

      for (const it of detalhesAll) {
        const o = it.obj;
        const det = it.det;

        if (!det) {
          console.log("Objetivo sem detalhe em cache", o);
          continue;
        }

        const listIndVend = Array.isArray(det?.individuais_vendedores)
          ? det.individuais_vendedores
          : [];

        const nomeObjetivo = o?.objetivo_nome || o?.nome || `Objetivo #${o?.id || ""}`;

        const metricas = ["qtd", "euros", "litros", "clientes"];

        for (const met of metricas) {
          // ============================
          // 🧑‍💼 ADMIN → LINHA AGREGADA
          // ============================
          if (isAdminLike) {
            let totalObj = 0;
            let totalReal = 0;

            for (const v of listIndVend) {
              const dados = v?.[met];
              totalObj += safeNum(dados?.objetivo);
              totalReal += safeNum(dados?.vendas);
            }

            if (totalObj <= 0) continue;

            const dif = totalReal - totalObj;
            const perc = totalObj > 0 ? (totalReal / totalObj) * 100 : 0;

            out.push({
              idobjetivo: o.id,
              metrica: met,
              tipo: "Vendedor",
              vendedor: "", // vazio (não faz sentido aqui)
              nome: `${nomeObjetivo} (${metricLabel(met)})`,
              inicio: toISO(o.dataini),
              fim: toISO(o.datafim),
              objetivo: totalObj,
              real: totalReal,
              dif,
              perc,
            });

            continue;
          }

          // ============================
          // 👤 UTILIZADOR → SÓ O SEU
          // ============================
          const minhaLinha = listIndVend.find((v) => String(v?.refvendedor) === String(refUser));

          if (!minhaLinha?.[met]) continue;

          const dados = minhaLinha[met];
          const objetivo = safeNum(dados?.objetivo);

          if (objetivo <= 0) continue;

          const real = safeNum(dados?.vendas);
          const dif = real - objetivo;
          const perc = objetivo > 0 ? (real / objetivo) * 100 : 0;

          out.push({
            idobjetivo: o.id,
            metrica: met,
            tipo: "Vendedor",
            vendedor: minhaLinha?.vendedor || "",
            nome: `${nomeObjetivo} (${metricLabel(met)})`,
            inicio: toISO(o.dataini),
            fim: toISO(o.datafim),
            objetivo,
            real,
            dif,
            perc,
          });
        }
      }

      // console.log("Objetivos dashboard out", out);
      // console.log("Objetivos dashboard out total", out.length);

      setRows(out);
      setCarregado(true);
    } catch (e) {
      setErro("Não foi possível carregar os objetivos.");
    } finally {
      setLoading(false);
    }
  }

  // async function recalcularObjetivos() {
  //   if (!canSee) return;

  //   setLoading(true);
  //   setErro("");

  //   try {
  //     await api.post("Sincronizacao/objetivos/rebuildcache", {});
  //     await loadFromCache();
  //   } catch (e) {
  //     setErro("Não foi possível recalcular os objetivos.");
  //   } finally {
  //     setLoading(false);
  //   }
  // }

  // async function load() {
  //   if (!canSee) return;

  //   setLoading(true);

  //   setErro("");

  //   try {
  //     // 1) lista objetivos (já respeita o filtro por vendedor)
  //     const body = isComercialRestrito
  //       ? { refvendedor: refUser, apenasVigentes: true }
  //       : { apenasVigentes: true };

  //     const resObj = await api.post("Comercial/SelObjetivos", body);
  //     const ativos = Array.isArray(resObj.data) ? resObj.data : resObj.data?.data || [];

  //     console.log("SelObjetivos resObj", resObj);
  //     console.log("SelObjetivos ativos", ativos);

  //     // 2) para cada objetivo ativo, ir buscar detalhes (reaproveitar a tua regra oficial)
  //     const detalhesAll = await Promise.all(
  //       ativos.map(async (o) => {
  //         try {
  //           const detRes = await api.post("Comercial/SelObjetivoDetalhes", { id: o.id });
  //           return { obj: o, det: detRes.data?.data ?? detRes.data }; // compatível com success/data ou array direto
  //         } catch {
  //           return { obj: o, det: null };
  //         }
  //       })
  //     );

  //     const out = [];

  //     for (const it of detalhesAll) {
  //       const o = it.obj;
  //       const det = it.det;

  //       if (!det) continue;

  //       const metFallback = pickMetricKey(o); // mantém a tua lógica atual como fallback
  //       const met = pickMetricKeyFromDetalhes(det, metFallback);

  //       const nomeObjetivo = o?.objetivo_nome || o?.nome || `Objetivo #${o?.id || ""}`;
  //       const nomeLinha = `${nomeObjetivo} (${metricLabel(met)})`;

  //       // helper para empurrar linha para a tabela
  //       const pushLine = (tipo, vendedor, objetivo, real, di, df) => {
  //         const objv = safeNum(objetivo);
  //         if (objv <= 0) return;

  //         const realv = safeNum(real);
  //         const dif = realv - objv;
  //         const perc = (realv / objv) * 100;

  //         out.push({
  //           idobjetivo: o.id,
  //           metrica: met,
  //           tipo,
  //           vendedor: vendedor || "",
  //           nome: nomeLinha,
  //           inicio: toISO(di),
  //           fim: toISO(df),
  //           objetivo: objv,
  //           real: realv,
  //           dif,
  //           perc,
  //         });
  //       };

  //       // -----------------------------
  //       // ADMIN-LIKE: 3 linhas agregadas
  //       // -----------------------------
  //       if (isAdminLike) {
  //         const fab = det?.fabricante?.[met];
  //         const eq = det?.equipa?.[met];
  //         const ind = det?.individuais?.[met];

  //         if (fab) pushLine("Fabricante", "", fab.objetivo, fab.vendas, o.dataini, o.datafim);
  //         if (eq) pushLine("Equipa", "", eq.objetivo, eq.vendas, o.dataini, o.datafim);
  //         if (ind) pushLine("Individuais", "", ind.objetivo, ind.vendas, o.dataini, o.datafim);

  //         continue;
  //       }

  //       // -------------------------------------------------------
  //       // COMERCIAL RESTRITO: só a linha do vendedor (se existir)
  //       // -------------------------------------------------------
  //       const listIndVend = Array.isArray(det?.individuais_vendedores)
  //         ? det.individuais_vendedores
  //         : [];
  //       const minhaLinha = listIndVend.find((v) => String(v?.refvendedor) === String(refUser));

  //       if (minhaLinha?.[met]) {
  //         // Linha individual (tem objetivo e real)
  //         pushLine(
  //           "Vendedor",
  //           minhaLinha.vendedor || "",
  //           minhaLinha[met].objetivo,
  //           minhaLinha[met].vendas,
  //           o.dataini,
  //           o.datafim
  //         );
  //       } else {
  //         // Não tem objetivo individual: mostrar contributo na equipa
  //         // (vamos buscar o REAL do vendedor na equipa via SelObjetivoEquipaDetalheVendedores)
  //         try {
  //           const eqRes = await api.post("Comercial/SelObjetivoEquipaDetalheVendedores", {
  //             id: o.id,
  //             tipo: met,
  //           });

  //           const eqRows = Array.isArray(eqRes.data) ? eqRes.data : eqRes.data?.data || [];
  //           const myEq = (Array.isArray(eqRows) ? eqRows : []).find(
  //             (r) => String(r?.refvendedor) === String(refUser)
  //           );

  //           const realMeu = safeNum(myEq?.valor);
  //           // Objetivo: aqui tens 2 opções:
  //           // A) usar objetivo da EQUIPA (faz sentido como "objetivo onde estou inserido")
  //           // B) usar 0 e mostrar só o real (mas então dif/% perdem sentido)
  //           const eqTot = det?.equipa?.[met];
  //           const objEquipa = safeNum(eqTot?.objetivo);

  //           pushLine(
  //             "Equipa (meu real)",
  //             myEq?.vendedor || "",
  //             objEquipa,
  //             realMeu,
  //             o.dataini,
  //             o.datafim
  //           );
  //         } catch {
  //           // se falhar, não adiciona linha
  //         }
  //       }
  //     }

  //     setRows(out);

  //     setCarregado(true);
  //   } catch (e) {
  //     setErro("Não foi possível carregar os objetivos.");
  //   } finally {
  //     setLoading(false);
  //   }
  // }

  useEffect(() => {
    if (!canSee) return;

    loadFromCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(() => {
    const cols = [
      {
        Header: "",
        id: "info",
        disableFilters: true,
        Cell: ({ row }) => {
          const r = row.original;

          if (!isAdminLike) return null;

          return (
            <Tooltip title="Ver detalhe por vendedor" arrow placement="top">
              <IconButton size="small" color="info" onClick={() => abrirInfo(r)}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
      { Header: "Tipo", accessor: "tipo", Filter: DefaultColumnFilter },
      { Header: "Nome", accessor: "nome", Filter: DefaultColumnFilter },
      { Header: "Início", accessor: "inicio", Filter: DefaultColumnFilter },
      { Header: "Fim", accessor: "fim", Filter: DefaultColumnFilter },
      { Header: "Objetivo", accessor: "objetivo", Filter: DefaultColumnFilter },
      {
        Header: !isAdminLike ? "Real (meu)" : "Real",
        accessor: "real",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => <b>{value}</b>,
      },
      {
        Header: "Dif.",
        accessor: "dif",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => {
          const v = safeNum(value);
          const txt = Number.isFinite(v) ? v.toFixed(2) : "";
          return <span style={{ color: v >= 0 ? "#4caf50" : "#d32f2f" }}>{txt}</span>;
        },
      },

      {
        Header: "%",
        accessor: "perc",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => (value == null ? "" : `${Number(value).toFixed(1)}%`),
      },
    ];

    if (!isAdminLike) {
      cols.splice(1, 0, {
        Header: "Vendedor",
        accessor: "vendedor",
        Filter: DefaultColumnFilter,
      });
    }

    return cols;
  }, [isAdminLike]);

  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);

  const tableInstance = useTable({ columns, data: rows, defaultColumn }, useFilters);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows: tableRows,
    prepareRow,
  } = tableInstance;

  const hasRows = carregado && tableRows.length > 0;

  if (!canSee) return null;

  return (
    <>
      <MDBox mb={2}>
        <Card>
          <MDBox p={2}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDBox>
                <MDTypography variant="h6" gutterBottom>
                  Objetivos
                </MDTypography>

                <MDTypography variant="caption" color="text">
                  Objetivos ativos ou recentes (até 7 dias).
                </MDTypography>
              </MDBox>

              {/* <MDTypography variant="button" color="text">
              {carregado ? `${tableRows.length} linhas` : ""}
              {loading ? " (a carregar...)" : ""}
            </MDTypography> */}

              {/* <MDButton
                color="info"
                size="small"
                onClick={recalcularObjetivos}
                disabled={loading}
                sx={{ minWidth: 190 }}
              >
                Recalcular Objetivos
              </MDButton> */}
            </MDBox>

            <MDBox
              sx={{
                ...(hasRows
                  ? {
                      maxHeight: 360,
                      overflowY: "auto",
                      borderRadius: 1,
                    }
                  : { overflowY: "visible" }),
                "& tbody tr:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
              }}
            >
              {loading ? (
                <MDBox py={2} display="flex" justifyContent="center">
                  <CircularProgress size={24} />
                </MDBox>
              ) : (
                <table
                  {...getTableProps()}
                  style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
                >
                  <thead>
                    {headerGroups.map((hg) => (
                      <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                        {hg.headers.map((col) => (
                          <th
                            {...col.getHeaderProps()}
                            key={col.id}
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                              background: "#fff",
                              textAlign: "left",
                              padding: "6px 8px",
                              fontSize: 12,
                              color: "#7b809a",
                              verticalAlign: "bottom",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                            }}
                          >
                            <div>{col.render("Header")}</div>

                            {!col.disableFilters && (
                              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                                {col.canFilter ? col.render("Filter") : null}
                              </div>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody {...getTableBodyProps()}>
                    {tableRows.map((r) => {
                      prepareRow(r);
                      return (
                        <tr
                          {...r.getRowProps()}
                          key={r.id}
                          style={{
                            borderBottom: "1px solid #f0f0f0",
                            transition: "background-color 0.15s",
                          }}
                        >
                          {r.cells.map((cell) => (
                            <td
                              {...cell.getCellProps()}
                              key={cell.column.id}
                              style={{
                                padding: "6px 8px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
      <Dialog
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
          Detalhe por vendedor
        </DialogTitle>

        <DialogContent dividers>
          {infoLoading && (
            <MDBox display="flex" alignItems="center" gap={1} py={2}>
              <CircularProgress size={20} />
              <span>A carregar…</span>
            </MDBox>
          )}

          {!infoLoading && !infoDetalhes && <div style={{ opacity: 0.8 }}>Sem dados.</div>}

          {!infoLoading && infoDetalhes && (
            <VendedoresMiniTable
              vendedores={infoDetalhes?.individuais_vendedores}
              tipoKey={infoRow?.metrica}
            />
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenInfo(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
